import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Truck, ChevronDown, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { APEX_LOGO } from "@/lib/brand";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TOOLS } from "@/pages/Tools";

async function openTmsWithSSO() {
  try {
    const { data } = await api.post("/sso/tms-token");
    if (data?.redirect_url) {
      window.open(data.redirect_url, "_blank", "noopener,noreferrer");
    } else {
      toast.error("TMS is not configured. Please contact ops.");
    }
  } catch (e) {
    console.error("[TMS SSO] token issue failed:", e);
    toast.error("Could not sign you into TMS. Please try again or contact ops.");
  }
}
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/technology", label: "Technology" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-[#2E7BC7]" : "text-slate-700 hover:text-[#0B3D6E]"}`;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <img src={APEX_LOGO} alt="Apex Supply Chain Solutions" className="h-14 w-auto object-contain transition-transform group-hover:scale-105" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.toLowerCase()}`}>
                {n.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-tools-trigger"
                className="text-sm font-medium text-slate-700 hover:text-[#0B3D6E] flex items-center gap-1"
              >
                Tools <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {TOOLS.map(t => {
                  const Icon = t.icon;
                  return (
                    <DropdownMenuItem
                      key={t.slug}
                      data-testid={`nav-tool-${t.slug}`}
                      onClick={() => navigate(`/tools/${t.slug}`)}
                      className="flex items-start gap-2 py-2"
                    >
                      <Icon className="h-4 w-4 text-[#2E7BC7] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800">{t.label}</div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#0B3D6E]"
                >
                  {user.name} <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-testid="menu-goto-portal"
                    onClick={() => navigate(user.role === "carrier" ? "/portal/carrier" : user.role === "admin" ? "/portal/admin" : "/portal/customer")}
                  >
                    My Portal
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-tms-login" onClick={() => navigate("/portal/tms")}>
                    TMS Portal
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="portal-menu-trigger"
                    className="text-sm font-medium text-slate-700 hover:text-[#0B3D6E] flex items-center gap-1"
                  >
                    Portals <ChevronDown className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem data-testid="menu-customer-login" onClick={() => navigate("/login?role=customer")}>Customer Login</DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-carrier-login" onClick={() => navigate("/login?role=carrier")}>Carrier Login</DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-tms-login" onClick={() => { toast.info("Sign in to access the TMS portal."); navigate("/login?next=tms"); }}>
                      TMS Portal
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            <Link to="/quote" className="btn-primary-apex" data-testid="nav-quote-cta">Get a Quote</Link>
          </div>

          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger data-testid="mobile-menu-trigger" aria-label="Open menu">
                <Menu className="h-6 w-6 text-[#0B3D6E]" />
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="mt-8 flex flex-col gap-4">
                  {NAV.map((n) => (
                    <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                      className="text-base font-medium text-slate-800"
                      data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
                      {n.label}
                    </Link>
                  ))}
                  <div className="h-px bg-gray-200 my-2" />
                  <div className="text-xs font-label tracking-[0.15em] uppercase text-slate-500">Tools</div>
                  {TOOLS.map(t => (
                    <Link key={t.slug} to={`/tools/${t.slug}`} onClick={() => setOpen(false)}
                      className="text-sm font-medium text-slate-800"
                      data-testid={`mobile-tool-${t.slug}`}>
                      {t.label}
                    </Link>
                  ))}
                  <div className="h-px bg-gray-200 my-2" />
                  {user ? (
                    <>
                      <Link to={user.role === "carrier" ? "/portal/carrier" : "/portal/customer"}
                        onClick={() => setOpen(false)} className="text-base font-medium text-slate-800">My Portal</Link>
                      <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }}
                        className="text-left text-base font-medium text-slate-800">Log out</button>
                    </>
                  ) : (
                    <>
                      <Link to="/login?role=customer" onClick={() => setOpen(false)} className="text-base font-medium text-slate-800">Customer Login</Link>
                      <Link to="/login?role=carrier" onClick={() => setOpen(false)} className="text-base font-medium text-slate-800">Carrier Login</Link>
                    </>
                  )}
                  <div className="h-px bg-gray-200 my-2" />
                  {user ? (
                    <button
                      type="button"
                      onClick={() => { setOpen(false); navigate("/portal/tms"); }}
                      className="text-left text-base font-medium text-[#2E7BC7]"
                      data-testid="mobile-tms-login-link"
                    >
                      TMS Portal
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        toast.info("Sign in to access the TMS portal.");
                        navigate("/login?next=tms");
                      }}
                      className="text-left text-base font-medium text-slate-800"
                      data-testid="mobile-tms-login-link"
                    >
                      TMS Portal
                    </button>
                  )}
                  <Link to="/quote" onClick={() => setOpen(false)} className="btn-primary-apex text-center">Get a Quote</Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
